import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { DateInput } from "@/components/DateInput";
import { OperatorMonth } from "@/components/OperatorMonth";
import { toast } from "sonner";
import { Printer, Trash2, ChevronDown, Pencil, Tag, CalendarRange, History, Users, Image as ImageIcon } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { MoreMenu } from "@/components/MoreMenu";
import { EmployeesManager, useEmployees, reloadEmployees } from "@/components/Employees";
import { TimesheetUpload } from "@/components/TimesheetUpload";
import { TimesheetEditDialog } from "@/components/TimesheetEditDialog";
import { PhotoLightbox } from "@/components/PhotoLightbox";
import { MaterialPrices } from "@/components/MaterialPrices";

const fmt = (iso) => (iso ? iso.split("-").reverse().join("/") : "");
const h = (n) => `${(n || 0).toLocaleString("it-IT", { maximumFractionDigits: 2 })} h`;
const photoUrl = (id) => `${API}/admin/timesheets/photo/${id}?auth=${localStorage.getItem("an_token")}`;

const VehicleRow = ({ v, i, onDelete }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border border-white/10 bg-[#181A22]" data-testid={`hours-vehicle-${i}`}>
      <div className="flex items-center pr-2">
      <button onClick={() => setOpen(!open)} className="min-w-0 flex-1 p-4 flex flex-wrap items-center gap-3 text-left" data-testid={`hours-vehicle-${i}-toggle`}>
        <div className="font-mono-tech text-xs bg-white/5 rounded px-2 py-1">{v.plate || "—"}</div>
        <div className="min-w-0 flex-1">
          <div className="font-semibold truncate">{v.job?.vehicle || v.vehicle || "Veicolo"}</div>
          <div className="text-xs text-muted-foreground truncate">{v.operators.map((o) => `${o.name} ${h(o.hours)}`).join(" · ")}{v.job ? ` · scheda ${v.job.number || ""} ${v.job.customer_name || ""}` : ""}</div>
        </div>
        <div className="text-lg font-bold text-[#FF9E00]" data-testid={`hours-vehicle-${i}-total`}>{h(v.hours)}</div>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <button onClick={() => onDelete(v)} className="p-2 rounded-full text-muted-foreground hover:text-[#F87171] hover:bg-[#E63946]/15 transition-colors" aria-label="Elimina ore di questo veicolo" data-testid={`hours-vehicle-${i}-delete`}><Trash2 className="w-4 h-4" /></button>
      </div>
      {open && (
        <div className="px-4 pb-4 space-y-1 text-sm">
          {v.entries.map((e, j) => (
            <div key={j} className="grid grid-cols-[80px_1fr_2fr_60px] gap-2 border-t border-white/5 pt-1">
              <span className="text-muted-foreground">{fmt(e.date)}</span><span>{e.operator}</span><span className="text-muted-foreground">{e.works}</span><span className="text-right font-semibold">{h(e.hours)}</span>
            </div>
          ))}
          {v.job && <Link to={`/admin/carrozzeria/${v.job.id}`} className="text-xs text-[#3B82F6] underline">Apri scheda accettazione</Link>}
        </div>
      )}
      {!v.job && (
        <div className="px-4 pb-3 -mt-1 flex flex-wrap items-center gap-2 text-xs" data-testid={`hours-vehicle-${i}-orphan`}>
          <span className="text-amber-400">Ore senza scheda accettazione</span>
          <Link to={`/admin/carrozzeria/nuova?${new URLSearchParams({ targa: v.plate || "", veicolo: v.vehicle || "", dal: v.entries[0]?.date || "" })}`}
            className="rounded-full bg-[#E63946] text-white px-3 py-1 font-semibold" data-testid={`hours-vehicle-${i}-create-job`}>Crea scheda e collega le ore</Link>
        </div>
      )}
    </div>
  );
};

export const HoursPanel = ({ onChange }) => {
  const [sum, setSum] = useState(null);
  const [sheets, setSheets] = useState([]);
  const [edit, setEdit] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [range, setRange] = useState({ date_from: "", date_to: "" });
  const [prices, setPrices] = useState(false);
  const [emps, setEmps] = useState(false);
  const { unknown } = useEmployees();
  const [showDates, setShowDates] = useState(false);
  const [showSheets, setShowSheets] = useState(false);
  const load = () => {
    onChange?.();
    reloadEmployees();
    api.get("/admin/timesheets/summary", { params: range }).then((r) => setSum(r.data)).catch(() => {});
    api.get("/admin/timesheets").then((r) => setSheets(r.data)).catch(() => {});
  };
  useEffect(load, [range]); // eslint-disable-line react-hooks/exhaustive-deps
  const remove = async (s) => {
    if (!window.confirm(`Eliminare la scheda ore di ${s.operator} del ${fmt(s.date)}?`)) return;
    try { await api.delete(`/admin/timesheets/${s.id}`); toast.success("Scheda ore eliminata"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const removeVehicle = async (v) => {
    const name = v.plate || v.vehicle || "senza targa";
    if (!window.confirm(`Eliminare tutte le ore di ${name} (${h(v.hours)})${range.date_from || range.date_to ? " nel periodo selezionato" : ""}?${v.job ? " La scheda accettazione resta." : ""}`)) return;
    try {
      const { data } = await api.delete(`/admin/timesheets/vehicle/${encodeURIComponent(v.key)}`, { params: range });
      toast.success(data.removed === 1 ? "Eliminata 1 riga di ore" : `Eliminate ${data.removed} righe di ore`); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="hours-panel">
      <div className="flex flex-wrap items-center gap-2">
        <TimesheetUpload onSaved={load} />
        <MoreMenu testid="hours-more" className="ml-auto" items={[
          { label: "Stampa scheda operatore vuota", icon: Printer, to: "/admin/carrozzeria/scheda-operatore", testid: "operator-sheet-link" },
          { label: "Dipendenti", icon: Users, onClick: () => setEmps(true), testid: "employees-open" },
          { label: "Prezzi materiali", icon: Tag, onClick: () => setPrices(true), testid: "material-prices-open" },
          { label: showDates ? "Togli filtro date" : "Filtra per date", icon: CalendarRange, onClick: () => { if (showDates) setRange({ date_from: "", date_to: "" }); setShowDates(!showDates); }, testid: "hours-dates-toggle" },
          { label: showSheets ? "Nascondi schede caricate" : "Schede ore caricate", icon: History, onClick: () => setShowSheets(!showSheets), testid: "hours-sheets-toggle" },
        ]} />
      </div>
      {unknown.length > 0 && (
        <button onClick={() => setEmps(true)} className="w-full text-left rounded-xl border border-[#FF9E00]/40 bg-[#FF9E00]/10 px-4 py-3 text-sm" data-testid="employees-unknown-banner">
          <b className="text-[#FF9E00]">{unknown.length} {unknown.length === 1 ? "nome" : "nomi"} operatore da sistemare</b> ({unknown.map((u) => u.name).join(", ")}) · tocca per collegarli all'elenco dipendenti
        </button>
      )}
      <Dialog open={prices} onOpenChange={setPrices}>
        <DialogContent className="max-w-lg w-[calc(100%-1rem)] rounded-2xl bg-[#12141A] border-white/10" data-testid="material-prices-dialog">
          <DialogHeader><DialogTitle>Prezzi materiali</DialogTitle><DialogDescription>Usati quando l'operatore scrive solo la quantità.</DialogDescription></DialogHeader>
          <MaterialPrices defaultOpen />
        </DialogContent>
      </Dialog>
      <Dialog open={emps} onOpenChange={(o) => { setEmps(o); if (!o) load(); }}>
        <DialogContent className="max-w-lg w-[calc(100%-1rem)] rounded-2xl bg-[#12141A] border-white/10 max-h-[90vh] overflow-y-auto" data-testid="employees-dialog">
          <DialogHeader><DialogTitle>Dipendenti</DialogTitle><DialogDescription>I nomi letti dalle foto vengono collegati in automatico a questo elenco.</DialogDescription></DialogHeader>
          <EmployeesManager />
        </DialogContent>
      </Dialog>

      <section>
        <div className="flex flex-wrap items-end gap-2 mb-3">
          <h3 className="font-display font-bold uppercase text-sm mr-auto">Ore per veicolo</h3>
          {showDates && <div className="grid grid-cols-2 gap-2 w-full sm:w-auto order-last sm:order-none">
          <DateInput value={range.date_from} onChange={(e) => setRange({ ...range, date_from: e.target.value })} placeholder="Dal" className="w-full sm:w-40 bg-[#181A22] border-white/10" data-testid="hours-from" aria-label="Dal" />
          <DateInput value={range.date_to} onChange={(e) => setRange({ ...range, date_to: e.target.value })} placeholder="Al" className="w-full sm:w-40 bg-[#181A22] border-white/10" data-testid="hours-to" aria-label="Al" />
          </div>}
          <div className="rounded-full bg-[#FF9E00]/15 text-[#FF9E00] px-4 py-2 text-sm font-bold" data-testid="hours-grand-total">Totale {h(sum?.total_hours)}</div>
        </div>
        {!sum ? <p className="text-sm text-muted-foreground">Caricamento…</p> : sum.vehicles.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="hours-empty">Nessuna ora registrata: stampa la scheda operatore, falla compilare e caricane la foto.</p>
        ) : <div className="space-y-2">{sum.vehicles.map((v, i) => <VehicleRow key={v.key} v={v} i={i} onDelete={removeVehicle} />)}</div>}
      </section>

      <OperatorMonth sheets={sheets} />

      {sheets.length > 0 && showSheets && (
        <section data-testid="hours-sheets">
          <h3 className="font-display font-bold uppercase text-sm mb-3">Schede ore caricate</h3>
          <div className="space-y-1.5">
            {sheets.map((s) => (
              <div key={s.id} className="rounded-lg border border-white/10 bg-[#181A22] px-3 py-2 flex items-center gap-3 text-sm" data-testid={`timesheet-${s.id}`}>
                <span className="text-muted-foreground w-20">{fmt(s.date)}</span>
                <span className="font-semibold flex-1 truncate">{s.operator} · {s.rows.length} veicoli</span>
                <span className="font-bold">{h(s.total_hours)}</span>
                {s.photo_id && <button onClick={() => setPhoto(photoUrl(s.photo_id))} className="p-1.5 rounded-full hover:bg-white/10" aria-label="Vedi foto" data-testid={`timesheet-photo-${s.id}`}><ImageIcon className="w-4 h-4" /></button>}
                <button onClick={() => setEdit(s)} className="p-1.5 rounded-full hover:bg-white/10" aria-label="Correggi" data-testid={`timesheet-edit-${s.id}`}><Pencil className="w-4 h-4" /></button>
                <button onClick={() => remove(s)} className="p-1.5 rounded-full hover:bg-[#E63946]/20" aria-label="Elimina" data-testid={`timesheet-delete-${s.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </section>
      )}
      <TimesheetEditDialog ts={edit} onClose={() => setEdit(null)} onSaved={load} />
      <PhotoLightbox src={photo} onClose={() => setPhoto(null)} />
    </div>
  );
};
